/**
 * V63.10: 파견/계약직 근무평가 탭 컴포넌트
 *
 * OrgEvaluationsPage의 "근무평가" 탭에서 렌더링.
 * - 파견직/계약직 대상자 목록
 * - 평가 생성 / 1차평가 / 2차평가(계약직) / HR 제출
 * - 평가 상세 모달 (PDF 양식 기반 폼)
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ModalOverlay } from '../../../shared/components/ModalOverlay';
import { FileText, CheckCircle, Plus, Send, Edit3, Eye, X } from 'lucide-react';
import { Employee } from '../../../shared/types/employee';
import {
  useOrgEvaluations,
  EvaluationItem,
  EvaluationType,
  EVALUATION_TYPE_LABELS,
  EVALUATION_STATUS_LABELS,
  EvaluationStatus,
} from '../../../shared/hooks/useEvaluations';
import { useAuthContext } from '../../../shared/hooks/useAuthContext';
import { useOrgLeaderCandidates } from '../../../shared/hooks/useOrgLeaderCandidates';
import { WorkEvaluationContent } from '../../../shared/components/WorkEvaluationContent';
// V68: 고용형태별 시작일/만료일 분기 헬퍼 (DISPATCH/CONTRACT/INTERN/REGULAR)
import { getCurrentEmploymentEndDate, getCurrentEmploymentStartDate } from '../../../shared/utils/employmentDates';

// ============================================
// Props
// ============================================

interface WorkEvaluationTabProps {
  employees: Employee[];
  scopeOrgUnitIds: string[];
  onEmployeeNameClick?: (employeeId: string, employeeName: string) => void;
}

// ============================================
// 헬퍼: 파견/계약직 필터
// ============================================

function isWorkEvalTarget(emp: Employee): boolean {
  const code = (emp.currentEmploymentTypeCode || '').toUpperCase();
  return code === 'DISPATCH' || code === 'CONTRACT';
}

function getEvalTypeForEmployee(emp: Employee): 'dispatch' | 'contract' | null {
  const code = (emp.currentEmploymentTypeCode || '').toUpperCase();
  if (code === 'DISPATCH') return 'dispatch';
  if (code === 'CONTRACT') return 'contract';
  return null;
}

// V68: 공유 헬퍼로 통일 (DISPATCH/CONTRACT/INTERN/REGULAR 모두 분기. 기존 INTERN 분기 누락 해결).
function getContractEndDate(emp: Employee): string {
  return getCurrentEmploymentEndDate(emp);
}

// V67: 입사일 = firstHireDate (최초입사일) 고정. 고용형태별 분기 제거.
function getHireDate(emp: Employee): string {
  return emp.firstHireDate || '-';
}

// V68: 공유 헬퍼로 통일 (DISPATCH/CONTRACT/INTERN/REGULAR 분기). 본 탭은 DISPATCH/CONTRACT 만 노출되나 일관성 차원.
function getContractStartDate(emp: Employee): string {
  return getCurrentEmploymentStartDate(emp);
}

// ============================================
// WorkEvaluationTab
// ============================================

export function WorkEvaluationTab({ employees, scopeOrgUnitIds, onEmployeeNameClick }: WorkEvaluationTabProps) {
  const {
    evaluations,
    loading,
    error,
    listOrgEvaluations,
    createWorkEvaluation,
    getEvaluationDetail,
    submitManagerEvaluation,
    submitSecondEvaluation,
    sendWorkEvalToUpper,
    approveWorkEvaluation,
  } = useOrgEvaluations();

  // V64.4: 현재 로그인 사용자 ID (상위 조직장 picker에서 본인 제외용)
  const { userId: currentUserId } = useAuthContext();

  // V64.4: 상위 조직장 후보 목록 (전용 CF — getEmployees 403 우회)
  // V62-diag: error/isLoading도 구독 — CF 실패 시 원인 파악용
  const { candidates: orgLeaderCandidates, error: leaderCandidatesError, isLoading: leaderCandidatesLoading } = useOrgLeaderCandidates(true);

  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [detailEval, setDetailEval] = useState<EvaluationItem | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // V64.5: 상위 조직장 상태 (모달 내 지정)
  const [selectedUpperId, setSelectedUpperId] = useState<string | null>(null);
  const [selectedUpperName, setSelectedUpperName] = useState<string | null>(null);
  // V64.3: 근로형태 선택 모달 상태
  const [typePickerTarget, setTypePickerTarget] = useState<Employee | null>(null);

  // V61-FIX: 모달 열려있을 때 훅 에러 → 모달 내부 submitError로 전파
  useEffect(() => {
    if (detailEval && error) {
      setSubmitError(error);
    }
  }, [error, detailEval]);

  // 데이터 로드
  useEffect(() => {
    listOrgEvaluations({ year: yearFilter });
  }, [listOrgEvaluations, yearFilter]);

  // 파견/계약직 대상 직원 필터
  const targetEmployees = useMemo(() => {
    // V64.3 DEBUG: 계약만료일 필드 확인 (첫 3명)
    const dispatchOrContract = employees.filter(e => isWorkEvalTarget(e));
    return dispatchOrContract
      .filter(e => e.status === 'active')
      .sort((a, b) => {
        // 1차: 계약만료일 오름차순 (임박순), 날짜 없으면 맨 아래
        const dateA = getContractEndDate(a);
        const dateB = getContractEndDate(b);
        const hasA = dateA !== '-';
        const hasB = dateB !== '-';
        if (hasA !== hasB) return hasA ? -1 : 1;
        if (hasA && hasB && dateA !== dateB) return dateA.localeCompare(dateB);
        // 2차: 이름 가나다순
        return (a.nameKo || '').localeCompare(b.nameKo || '', 'ko');
      });
  }, [employees]);

  // 직원별 평가 매핑
  const evalByEmployee = useMemo(() => {
    const map: Record<string, EvaluationItem | null> = {};
    for (const emp of targetEmployees) {
      const evalType = getEvalTypeForEmployee(emp);
      const found = evaluations.find(
        e => e.targetUserId === emp.id &&
          (e.templateType === evalType || e.templateType === 'dispatch_contract') &&
          e.evaluationYear === yearFilter
      );
      map[emp.id] = found || null;
    }
    return map;
  }, [targetEmployees, evaluations, yearFilter]);

  // V64.5: 평가 생성 (upper 없이도 생성 성공 → 상세 모달 자동 오픈)
  const handleCreate = useCallback(async (emp: Employee, templateTypeOverride?: 'dispatch' | 'contract') => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await createWorkEvaluation({
        targetUserId: emp.id,
        evaluationYear: yearFilter,
        templateTypeOverride,
      });
      if (result) {
        setTypePickerTarget(null);
        await listOrgEvaluations({ year: yearFilter });
        // V64.5: 생성 직후 상세 모달 자동 오픈
        if (result.evaluationId) {
          const detail = await getEvaluationDetail(result.evaluationId);
          if (detail) {
            setDetailEval(detail);
            setFormData(detail.managerEvaluation || {});
            setSelectedUpperId(detail.upperEvaluatorId || null);
            setSelectedUpperName(detail.upperEvaluatorName || null);
            setSubmitError(null);
          }
        }
      }
    } catch (err: any) {
      const msg = err?.message || '평가 생성 실패';
      if (msg.includes('근로형태') && !templateTypeOverride) {
        // V64.3: 근로형태 미설정 → 타입 선택 모달
        setTypePickerTarget(emp);
      } else {
        setSubmitError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }, [createWorkEvaluation, getEvaluationDetail, listOrgEvaluations, yearFilter, submitting]);

  // 상세 보기
  const handleViewDetail = useCallback(async (evalItem: EvaluationItem) => {
    const detail = await getEvaluationDetail(evalItem.id);
    if (detail) {
      setDetailEval(detail);
      // 기존 데이터로 formData 초기화
      setFormData(detail.managerEvaluation || {});
      // V64.5: upper 상태 초기화
      setSelectedUpperId(detail.upperEvaluatorId || null);
      setSelectedUpperName(detail.upperEvaluatorName || null);
      setSubmitError(null);
    }
  }, [getEvaluationDetail]);

  // 1차 평가 제출
  const handleManagerSubmit = useCallback(async () => {
    if (!detailEval || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const success = await submitManagerEvaluation({
        evaluationId: detailEval.id,
        managerEvaluation: formData,
        // V64.5: 상위 조직장 ID 함께 전송
        upperEvaluatorId: selectedUpperId || undefined,
      });
      if (success) {
        setDetailEval(null);
        await listOrgEvaluations({ year: yearFilter });
      }
    } catch (err: any) {
      setSubmitError(err?.message || '제출 실패');
    } finally {
      setSubmitting(false);
    }
  }, [detailEval, formData, selectedUpperId, submitManagerEvaluation, listOrgEvaluations, yearFilter, submitting]);

  // V63.11: 상위 조직장에게 전달
  const handleSendToUpper = useCallback(async (evalItem: EvaluationItem) => {
    if (!window.confirm('상위 조직장에게 전달하시겠습니까?')) return;
    setSubmitting(true);
    try {
      await sendWorkEvalToUpper(evalItem.id);
      await listOrgEvaluations({ year: yearFilter });
    } finally {
      setSubmitting(false);
    }
  }, [sendWorkEvalToUpper, listOrgEvaluations, yearFilter]);

  // V63.11: 상위 조직장 승인/반려
  const handleUpperDecision = useCallback(async (evalItem: EvaluationItem, decision: 'approved' | 'rejected') => {
    const label = decision === 'approved' ? '승인' : '반려';
    if (!window.confirm(`${label}하시겠습니까?`)) return;
    setSubmitting(true);
    try {
      await approveWorkEvaluation({ evaluationId: evalItem.id, decision });
      await listOrgEvaluations({ year: yearFilter });
    } finally {
      setSubmitting(false);
    }
  }, [approveWorkEvaluation, listOrgEvaluations, yearFilter]);

  // 상태 뱃지
  const statusBadge = (status: EvaluationStatus) => {
    const colors: Record<string, { bg: string; text: string }> = {
      draft: { bg: '#FEF3C7', text: '#92400E' },
      manager_submitted: { bg: '#DBEAFE', text: '#1E40AF' },
      second_submitted: { bg: '#E0E7FF', text: '#3730A3' },
      sent_to_upper: { bg: '#FDE68A', text: '#92400E' },
      sent_to_hr: { bg: '#E9D5FF', text: '#6B21A8' },
      hr_approved: { bg: '#D1FAE5', text: '#065F46' },
      hr_rejected: { bg: '#FEE2E2', text: '#991B1B' },
      rejected: { bg: '#FEE2E2', text: '#991B1B' },
    };
    const c = colors[status] || { bg: '#F3F4F6', text: '#374151' };
    return (
      <span style={{
        padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
        background: c.bg, color: c.text,
      }}>
        {EVALUATION_STATUS_LABELS[status] || status}
      </span>
    );
  };

  return (
    <div>
      {/* 연도 필터 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <label style={{ fontWeight: 600, fontSize: 14 }}>평가 연도:</label>
        <select
          value={yearFilter}
          onChange={e => setYearFilter(Number(e.target.value))}
          style={{ padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 14 }}
        >
          {[yearFilter - 1, yearFilter, yearFilter + 1].map(y => (
            <option key={y} value={y}>{y}년</option>
          ))}
        </select>
        <span style={{ fontSize: 13, color: '#6B7280' }}>
          대상: 파견직/계약직 {targetEmployees.length}명
        </span>
      </div>

      {error && !detailEval && (
        <div style={{ padding: 12, background: '#FEE2E2', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#991B1B' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>로딩 중...</div>
      ) : targetEmployees.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
          <p>파견직/계약직 직원이 없습니다.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>이름</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>부서</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>근로형태</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>입사일</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>계약시작일</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>계약만료일</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>평가 상태</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>점수</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>액션</th>
              </tr>
            </thead>
            <tbody>
              {targetEmployees.map(emp => {
                const evalItem = evalByEmployee[emp.id];
                const evalType = getEvalTypeForEmployee(emp);
                return (
                  <tr key={emp.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <button
                        onClick={() => onEmployeeNameClick?.(emp.id, emp.nameKo || '')}
                        style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 500, fontSize: 13, padding: 0 }}
                      >
                        {emp.nameKo || '-'}
                      </button>
                    </td>
                    <td style={{ padding: '10px 12px' }}>{emp.orgUnitName || '-'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                        background: evalType === 'dispatch' ? '#FEF3C7' : evalType === 'contract' ? '#DBEAFE' : '#F3F4F6',
                        color: evalType === 'dispatch' ? '#92400E' : evalType === 'contract' ? '#1E40AF' : '#6B7280',
                      }}>
                        {evalType === 'dispatch' ? '파견직' : evalType === 'contract' ? '계약직' : '미설정'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 12 }}>{getHireDate(emp)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 12 }}>{getContractStartDate(emp)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 12 }}>{getContractEndDate(emp)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {evalItem ? statusBadge(evalItem.status) : (
                        <span style={{ color: '#9CA3AF', fontSize: 12 }}>미생성</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 12, fontWeight: 600 }}>
                      {evalItem?.weightedTotalScore != null ? evalItem.weightedTotalScore.toFixed(2) :
                        evalItem?.managerTotals?.grandTotal != null ? evalItem.managerTotals.grandTotal.toFixed(2) :
                          '-'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {renderActions(emp, evalItem)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 평가 상세 모달 */}
      {detailEval && (
        <WorkEvaluationModal
          evaluation={detailEval}
          formData={formData}
          onFormDataChange={setFormData}
          onSubmit={handleManagerSubmit}
          onClose={() => { setDetailEval(null); setSubmitError(null); }}
          submitting={submitting}
          submitError={submitError}
          orgLeaderCandidates={orgLeaderCandidates}
          leaderCandidatesError={leaderCandidatesError}
          leaderCandidatesLoading={leaderCandidatesLoading}
          currentUserId={currentUserId || ''}
          targetEmployee={detailEval ? employees.find(e => e.id === detailEval.targetUserId) : undefined}
          selectedUpperId={selectedUpperId}
          selectedUpperName={selectedUpperName}
          onUpperChange={(id, name) => { setSelectedUpperId(id); setSelectedUpperName(name); }}
        />
      )}

      {/* V64.3: 근로형태 선택 모달 */}
      {typePickerTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'white', borderRadius: 12, padding: 24, width: 400,
            display: 'flex', flexDirection: 'column', gap: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                근로형태 선택
              </h3>
              <button
                onClick={() => setTypePickerTarget(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6B7280' }}
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>
              <strong>{typePickerTarget.nameKo}</strong>님의 근로형태가 설정되지 않았습니다. 평가 유형을 선택해 주세요.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => handleCreate(typePickerTarget, 'dispatch')}
                disabled={submitting}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  border: '2px solid #F59E0B', background: '#FEF3C7', color: '#92400E', cursor: 'pointer',
                }}
              >
                파견직
              </button>
              <button
                onClick={() => handleCreate(typePickerTarget, 'contract')}
                disabled={submitting}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  border: '2px solid #3B82F6', background: '#DBEAFE', color: '#1E40AF', cursor: 'pointer',
                }}
              >
                계약직
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ============================================
  // V64.7: 역할 기반 액션 버튼 렌더링
  // ============================================
  function renderActions(emp: Employee, evalItem: EvaluationItem | null) {
    if (!evalItem) {
      return (
        <button
          onClick={() => handleCreate(emp)}
          disabled={submitting}
          style={{
            padding: '4px 12px', borderRadius: 4, fontSize: 12, fontWeight: 500,
            border: 'none', background: '#10B981', color: 'white', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
        >
          <Plus size={12} /> 평가 생성
        </button>
      );
    }

    // V64.7: 역할 판정 (evaluation 문서 필드 기반)
    // V308: 전배 후 새 조직장도 creator 동등 권한 (scope 내 직원 평가 관리)
    const viewerId = currentUserId;
    const isOriginalCreator = viewerId === evalItem.evaluatorId;
    const isUpper = viewerId === evalItem.upperEvaluatorId;
    const isSecond = viewerId === evalItem.secondEvaluatorId;
    // V308: 이 탭은 OrgEvaluationsPage 내에서만 렌더되므로,
    //       employees에 포함된 직원 = 현재 조직장 스코프 내 직원
    const isInScope = employees.some(e => e.id === evalItem.targetUserId);
    const isCreator = isOriginalCreator || (!isUpper && !isSecond && isInScope);

    const buttons: React.ReactNode[] = [];
    const status = evalItem.status;

    // --- 1) draft / rejected / hr_rejected: creator만 작성/수정, 나머지 보기만 ---
    if (status === 'draft' || status === 'rejected' || status === 'hr_rejected') {
      if (isCreator) {
        buttons.push(
          <button
            key="edit"
            onClick={() => handleViewDetail(evalItem)}
            style={{
              padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
              border: 'none', background: '#3B82F6', color: 'white', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            {status === 'rejected' || status === 'hr_rejected' ? '평가 수정' : '평가 작성'}
          </button>
        );
      } else {
        buttons.push(
          <button
            key="view"
            onClick={() => handleViewDetail(evalItem)}
            style={{
              padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
              border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <Eye size={12} /> 보기
          </button>
        );
      }

    // --- 2) manager_submitted / second_submitted: creator는 수정+전달, 나머지 보기 ---
    } else if (status === 'manager_submitted' || status === 'second_submitted') {
      // V263: contract도 manager_submitted에서 상위 조직장 전달 허용
      const canSend = status === 'manager_submitted' || status === 'second_submitted';
      const hasUpper = !!evalItem.upperEvaluatorId;

      if (isCreator) {
        // V64.9: 전달 전 수정 가능 — "평가 수정" 버튼
        buttons.push(
          <button
            key="edit"
            onClick={() => handleViewDetail(evalItem)}
            style={{
              padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
              border: 'none', background: '#3B82F6', color: 'white', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            평가 수정
          </button>
        );
        // 상위 조직장 전달 버튼
        if (canSend) {
          buttons.push(
            <button
              key="upper"
              onClick={() => hasUpper && handleSendToUpper(evalItem)}
              disabled={submitting || !hasUpper}
              title={!hasUpper ? '평가 상세에서 상위 조직장을 선택한 후 전달할 수 있습니다.' : ''}
              style={{
                padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                border: 'none', background: !hasUpper ? '#D1D5DB' : '#F59E0B', color: 'white',
                cursor: !hasUpper ? 'not-allowed' : 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              상위 조직장 전달
            </button>
          );
        }
      } else {
        // 비작성자: 보기만
        buttons.push(
          <button
            key="view"
            onClick={() => handleViewDetail(evalItem)}
            style={{
              padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
              border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <Eye size={12} /> 보기
          </button>
        );
      }

    // --- 3) sent_to_upper: upper만 승인/반려, creator는 "대기중" ---
    } else if (status === 'sent_to_upper') {
      if (isUpper) {
        buttons.push(
          <button
            key="approve"
            onClick={() => handleUpperDecision(evalItem, 'approved')}
            disabled={submitting}
            style={{
              padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
              border: 'none', background: '#10B981', color: 'white', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <CheckCircle size={12} /> 승인
          </button>,
          <button
            key="reject"
            onClick={() => handleUpperDecision(evalItem, 'rejected')}
            disabled={submitting}
            style={{
              padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
              border: 'none', background: '#EF4444', color: 'white', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <X size={12} /> 반려
          </button>
        );
      } else if (isCreator) {
        buttons.push(
          <span
            key="waiting"
            style={{
              padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
              background: '#F3F4F6', color: '#6B7280',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            상위 조직장 확인 대기
          </span>
        );
      }
      // 보기 버튼
      buttons.push(
        <button
          key="view"
          onClick={() => handleViewDetail(evalItem)}
          style={{
            padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
            border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
        >
          <Eye size={12} /> 보기
        </button>
      );

    // --- 4) sent_to_hr / hr_approved / hr_rejected: 보기만 ---
    } else {
      buttons.push(
        <button
          key="view"
          onClick={() => handleViewDetail(evalItem)}
          style={{
            padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
            border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
        >
          <Eye size={12} /> 보기
        </button>
      );
    }

    return <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>{buttons}</div>;
  }
}

// ============================================
// 평가 상세 모달 (PDF 양식 기반)
// ============================================

interface WorkEvaluationModalProps {
  evaluation: EvaluationItem;
  formData: Record<string, any>;
  onFormDataChange: (data: Record<string, any>) => void;
  onSubmit: () => void;
  onClose: () => void;
  submitting: boolean;
  submitError: string | null;
  // V64.5: 상위 조직장 지정
  orgLeaderCandidates: { id: string; nameKo: string; orgUnitName: string; jobTitle: string; assignedRoleIds: string[] }[];
  leaderCandidatesError?: string | null;
  leaderCandidatesLoading?: boolean;
  currentUserId: string;
  // V64.5.2: 대상자 계약일 표시
  targetEmployee?: Employee;
  selectedUpperId: string | null;
  selectedUpperName: string | null;
  onUpperChange: (id: string, name: string) => void;
}

function WorkEvaluationModal({
  evaluation,
  formData,
  onFormDataChange,
  onSubmit,
  onClose,
  submitting,
  submitError,
  orgLeaderCandidates,
  leaderCandidatesError,
  leaderCandidatesLoading,
  currentUserId,
  targetEmployee,
  selectedUpperId,
  selectedUpperName,
  onUpperChange,
}: WorkEvaluationModalProps) {
  // V64.9: 상위 조직장 전달 전까지 creator 수정 가능, 전달 후 readOnly
  const isCreatorInModal = currentUserId === evaluation.evaluatorId;
  const isEditable = isCreatorInModal && ['draft', 'rejected', 'hr_rejected', 'manager_submitted', 'second_submitted'].includes(evaluation.status);
  const isDispatch = evaluation.templateType === 'dispatch';

  const handleItemChange = (itemId: string, value: any) => {
    onFormDataChange({ ...formData, [itemId]: value });
  };

  return (
    <ModalOverlay onClose={onClose} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: 'white', borderRadius: 12, width: '95%', maxWidth: 1100, maxHeight: '90vh',
        overflow: 'auto', padding: 0,
      }}>
        {/* 헤더 */}
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, background: 'white', zIndex: 10,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>
              {isDispatch ? '파견직 근무평가서' : '계약직 근무평가서'}
            </h2>
            <span style={{ fontSize: 12, color: '#6B7280' }}>
              {evaluation.targetUserName} · {evaluation.evaluationYear}년
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {/* V267: 공통 평가서 콘텐츠 */}
          <WorkEvaluationContent
            evaluation={evaluation}
            formData={formData}
            isEditable={isEditable}
            onItemChange={handleItemChange}
            targetEmployee={targetEmployee}
            hideUpperEvaluator
          />

          {/* V64.5: 제출 대상(상위 조직장) 지정 */}
          <UpperEvaluatorSection
            evaluation={evaluation}
            isEditable={isEditable}
            orgLeaderCandidates={orgLeaderCandidates}
            leaderCandidatesError={leaderCandidatesError}
            leaderCandidatesLoading={leaderCandidatesLoading}
            currentUserId={currentUserId}
            selectedUpperId={selectedUpperId}
            selectedUpperName={selectedUpperName}
            onUpperChange={onUpperChange}
          />

          {/* 에러 메시지 */}
          {submitError && (
            <div style={{ padding: 12, background: '#FEE2E2', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#991B1B' }}>
              {submitError}
            </div>
          )}
        </div>

        {/* 푸터 버튼 */}
        <div style={{
          padding: '12px 24px', borderTop: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          position: 'sticky', bottom: 0, background: 'white',
        }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 20px', border: '1px solid #D1D5DB', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 13 }}
          >
            닫기
          </button>
          {isEditable && (
            <button
              onClick={onSubmit}
              disabled={submitting}
              style={{
                padding: '8px 20px', border: 'none', borderRadius: 6,
                background: submitting ? '#9CA3AF' : '#3B82F6', color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              {submitting ? '제출 중...' : (evaluation.status === 'draft' ? '평가 제출' : '수정 제출')}
            </button>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// V64.5: 상위 조직장 지정 섹션
// ============================================

function UpperEvaluatorSection({
  evaluation,
  isEditable,
  orgLeaderCandidates,
  leaderCandidatesError,
  leaderCandidatesLoading,
  currentUserId,
  selectedUpperId,
  selectedUpperName,
  onUpperChange,
}: {
  evaluation: EvaluationItem;
  isEditable: boolean;
  orgLeaderCandidates: WorkEvaluationModalProps['orgLeaderCandidates'];
  leaderCandidatesError?: string | null;
  leaderCandidatesLoading?: boolean;
  currentUserId: string;
  selectedUpperId: string | null;
  selectedUpperName: string | null;
  onUpperChange: (id: string, name: string) => void;
}) {
  const displayName = selectedUpperName || evaluation.upperEvaluatorName || null;
  const displayId = selectedUpperId || evaluation.upperEvaluatorId || null;
  const isAssigned = !!displayId;

  // V285: 미지정 + 편집 가능 시 picker 자동 오픈 (수동 선택 유도)
  const [isPickerOpen, setIsPickerOpen] = useState(!isAssigned && isEditable);
  const [searchText, setSearchText] = useState('');

  // V313: assignedRoleIds 필터 제거 — BE가 parent chain leaderId + self만 반환하므로
  // FE에서 auth_roleAssignments 기반 재필터는 불필요하며, leaderId는 있으나
  // auth_roleAssignments에 역할이 없는 상위 조직장이 누락되는 원인이었음

  // V62-fix: 기존 지정된 상위 조직장이 역할 기반 후보에 없을 때 fallback 포함
  // 원인: V62 이전 필드 기반으로 지정된 upperEvaluatorId가
  //       역할 기반 listOrgLeaderCandidates에 미포함되어 재검색 불가
  const enrichedCandidates = useMemo(() => {
    const base = [...orgLeaderCandidates];
    const existingUpperId = evaluation.upperEvaluatorId;
    if (existingUpperId && !base.some(c => c.id === existingUpperId)) {
      base.push({
        id: existingUpperId,
        nameKo: evaluation.upperEvaluatorName || '',
        orgUnitName: evaluation.upperEvaluatorOrgUnitName || '',
        jobTitle: evaluation.upperEvaluatorJobTitle || '',
        assignedRoleIds: [],
      });
    }
    return base;
  }, [orgLeaderCandidates, evaluation.upperEvaluatorId, evaluation.upperEvaluatorName,
      evaluation.upperEvaluatorOrgUnitName, evaluation.upperEvaluatorJobTitle]);

  const filteredCandidates = enrichedCandidates
    .filter(e =>
      e.id !== evaluation.targetUserId &&   // 평가대상 제외
      (searchText === '' || (e.nameKo || '').includes(searchText))
    )
    .sort((a, b) => (a.nameKo || '').localeCompare(b.nameKo || ''))
    .slice(0, 30);

  return (
    <div style={{
      marginBottom: 16, padding: '12px 16px', borderRadius: 8,
      border: `1px solid ${isAssigned ? '#D1D5DB' : '#F59E0B'}`,
      background: isAssigned ? '#F9FAFB' : '#FFFBEB',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isPickerOpen ? 8 : 0 }}>
        <div style={{ fontSize: 13 }}>
          <span style={{ fontWeight: 600 }}>제출 대상(상위 조직장): </span>
          {isAssigned ? (
            <span style={{ color: '#111827' }}>{displayName || displayId}</span>
          ) : (
            <span style={{ color: '#D97706', fontWeight: 600 }}>미지정</span>
          )}
          {!isAssigned && (
            <span style={{ display: 'block', fontSize: 12, color: '#92400E', marginTop: 2 }}>
              {isEditable
                ? '자동으로 지정되지 않았습니다. 아래에서 직접 선택해주세요.'
                : '상위 조직장을 지정해야 전달할 수 있습니다.'}
            </span>
          )}
        </div>
        {isEditable && (
          <button
            onClick={() => { setIsPickerOpen(!isPickerOpen); setSearchText(''); }}
            style={{
              padding: '4px 12px', borderRadius: 4, fontSize: 12, fontWeight: 500,
              border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer',
            }}
          >
            {isPickerOpen ? '닫기' : '변경'}
          </button>
        )}
      </div>
      {isPickerOpen && (
        <div>
          <input
            type="text"
            placeholder="이름 검색..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{
              width: '100%', padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 4,
              fontSize: 13, outline: 'none', marginBottom: 6, boxSizing: 'border-box',
            }}
          />
          <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 4 }}>
            {leaderCandidatesLoading ? (
              <div style={{ padding: '10px 12px', fontSize: 12, color: '#6B7280', textAlign: 'center' }}>
                후보 목록 로딩 중...
              </div>
            ) : leaderCandidatesError ? (
              <div style={{ padding: '10px 12px', fontSize: 12, color: '#DC2626', textAlign: 'center' }}>
                후보 조회 실패: {leaderCandidatesError}
              </div>
            ) : filteredCandidates.length === 0 ? (
              <div style={{ padding: '10px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>
                후보가 없습니다
              </div>
            ) : (
              filteredCandidates.map(e => (
                <button
                  key={e.id}
                  onClick={() => {
                    onUpperChange(e.id, e.nameKo || '');
                    setIsPickerOpen(false);
                    setSearchText('');
                  }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                    border: 'none', borderBottom: '1px solid #F3F4F6',
                    background: e.id === displayId ? '#EFF6FF' : 'white',
                    cursor: 'pointer', fontSize: 13,
                  }}
                  onMouseEnter={ev => { if (e.id !== displayId) ev.currentTarget.style.background = '#F9FAFB'; }}
                  onMouseLeave={ev => { if (e.id !== displayId) ev.currentTarget.style.background = 'white'; }}
                >
                  <span style={{ fontWeight: 600 }}>{e.nameKo || '-'}</span>
                  <span style={{ color: '#6B7280', marginLeft: 8, fontSize: 12 }}>
                    {e.orgUnitName || ''} {e.jobTitle || ''}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// V267: 스타일 상수는 WorkEvaluationContent.tsx로 이동
